import { UploadFile } from "../Upload/Upload";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { v4 as uuid } from "uuid";


export type ChatComponentFiles = UploadFile & {
  bucket: string;
  key: string;
  url: string;
  documentId?: number;
  /** Set instead of documentId for a file still in the intake queue. */
  documentQueueId?: string;
};

interface ChatStore {
  // Store states
  localChatId: number;
  fileList: ChatComponentFiles[];
  autoScroll: boolean;
  storedPromptSearch: string | null;
  saveButtonState: null | "saved" | "saving";

  // Actions
  setLocalChatId: (id: number) => void;
  setFileList: (
    files:
      | ChatComponentFiles[]
      | ((prev: ChatComponentFiles[]) => ChatComponentFiles[])
  ) => void;
  setAutoScroll: (scroll: boolean) => void;
  setStoredPromptSearch: (prompt: string | null) => void;
  setSaveButtonState: (state: null | "saved" | "saving") => void;
}

const initialState = {
  localChatId: 0,
  fileList: [],
  autoScroll: true,
  storedPromptSearch: null,
  saveButtonState: null,
};

const createChatStore = (storeId: string) =>
  create<ChatStore>()(set => ({
    // Initial states
    ...initialState,

    // Actions
    setLocalChatId: id => set({ localChatId: id }),
    setFileList: (
      files:
        | ChatComponentFiles[]
        | ((prev: ChatComponentFiles[]) => ChatComponentFiles[])
    ) =>
      set(state => ({
        fileList: typeof files === "function" ? files(state.fileList) : files,
      })),
    setAutoScroll: scroll => set({ autoScroll: scroll }),
    setStoredPromptSearch: prompt => set({ storedPromptSearch: prompt }),
    setSaveButtonState: state => set({ saveButtonState: state }),
  }));

// Store instance management
const stores = new Map<string, ReturnType<typeof createChatStore>>();

export const useChatStore = (chatStoreId: string) => {
  if (!stores.has(chatStoreId)) {
    stores.set(chatStoreId, createChatStore(chatStoreId));
  }

  return stores.get(chatStoreId)!;
};

export const cleanupChatStore = (chatStoreId: string) => {
  const store = stores.get(chatStoreId);
  if (store) {
    // Optional: Reset store state before removal
    store.setState(initialState);
    // Remove store instance
    stores.delete(chatStoreId);
  }
};
